import Handlebars from "utilities/vendor/handlebars/4.7.7/js/handlebars";
import hbs from "utilities/vendor/handlebars/4.7.7/js/handlebars";
/* global Handlebars */

/**
 * @const STRINGS
 * @description constant for storing message strings
 */
const STRINGS = {
  NO_ACCORDION_CONTENT_ERROR: 'No accordion JCR objects provided to helper-accordion.',
  NO_PANEL_CONTENT_ERROR: '<strong>Error:</strong> No panel content available.',
  DEFAULT_ADA_ERROR_TEXT: ', contains an error message',
  DEFAULT_ADA_WARNING_TEXT: ', contains a warning message',
  DEFAULT_ADA_SUCCESS_TEXT: ', contains a success message',
};

/**
 * Loops through the JCR contexts and matches the context for the current Panel content, then
 * compiles and returns the content into a Handlebars template string.
 * @param {object} panel - Object of JCR parameters for the current Panel.
 * @param {object} contexts - JCR contexts provided by the Handlebars template.
 * @param {function} template - Handlebars template used to compile the Panel content.
 * @returns {string} Compiled Handlebars template string with dynamic Panel content.
 */
function getDynamicContent(panel, contexts, template) {
  let panelContent;
  let panelContext;

  if (panel.dynamicContent === 'partial' && panel.content instanceof Object) {
    panelContent = template(panel.content);
  } else if (contexts.length > 0) {
    // Check to see if the value provided within panel.content matches the JCR path.
    contexts.forEach((context) => {
      if (panel.content === context.jcr) {
        panelContext = context;
      }
    });

    // If path for the partial context is missing output an error message.
    if (!panelContext) {
      panelContent = STRINGS.NO_PANEL_CONTENT_ERROR;
    } else if (panel.dynamicContent === 'partial') {
      panelContent = template(panelContext);
    } else if (panel.dynamicContent === 'markdown') {
      panelContent = template({
        content: panelContext,
      });
    } else {
      panelContent = panel.content;
    }
  } else {
    panelContent = STRINGS.NO_PANEL_CONTENT_ERROR;
  }

  return panelContent;
}

/**
 * @const {object} helpers
 * @description Exposes our Handlebars helper and the name we'll call it by.
 */
const helpers = {
  /**
   * @method helpers.accordion
   * @param {object} jcr JSON object used to build the accordion.
   * @returns {Node} `Handlebars.SafeString` version of `accordionWrap` element.
   * @description This pieces together all the HTML elements needed to make an accordion.
   */
  accordion: function accordionHelper(jcr, ...args) {
    if (!jcr) {
      throw new Error(STRINGS.NO_ACCORDION_CONTENT_ERROR);
    }

    // Combine all the additional arguments into an array of contexts for the partials/Markdown
    // content files.
    const jcrContexts = Array.prototype.slice.call(args, 0, -1);

    // Set defaults if JCR options do not exist.
    const type = jcr.options.type ? jcr.options.type : 'tabs';
    const oneAtATime = jcr.options.oneAtATime ? jcr.options.oneAtATime : '';
    const expandCollapseLink = jcr.options.expandCollapseLink
      ? jcr.options.expandCollapseLink
      : 'auto';

    // Expand and Collapse link text
    const expandLinkText = (jcr.expandLinkText ? jcr.expandLinkText : 'Expand all');
    const collapseLinkText = (jcr.collapseLinkText ? jcr.collapseLinkText : 'Collapse all');

    const expandLinkBactmln = (jcr.expandLinkBactmln ? jcr.expandLinkBactmln : null);
    const collapseLinkBactmln = (jcr.collapseLinkBactmln ? jcr.collapseLinkBactmln : null);

    // enable collapse of innerPanels via JCR
    const enableExpandCollapseInnerPanels = jcr.options && jcr.options.expandCollapseInnerPanels === true
      ? `data-expand-collapse-inner-panels="true"`
      : '';

    // Overall wrapper.
    let accordionWrap = `
      <section
        class="accordion-wrapper"
        id="${jcr.id}"
        data-accordion-type="${type}"
        data-one-at-a-time="${oneAtATime}"
        ${enableExpandCollapseInnerPanels}
      >
    `;

    // Optional accordion title.
    const titleHeadingTag = jcr.titleHeadingTag ? jcr.titleHeadingTag : 'h3';
    const accordionTitle = jcr.title ? `<${titleHeadingTag}>${jcr.title}</${titleHeadingTag}>` : '';

    // Hidden `break-out` link used to support display of all data for SEO.
    const breakoutLink = `
      <a
        href="javascript:void(0);"
        class="spa-hidden"
        aria-hidden="true"
        data-breakout
      >Expand all panels</a>
    `;

    // Include the `break-out` links for all accordion groups.
    let accordionGroupControls = `${breakoutLink}`;

    // If the accordion group is configured with visible expand/collapse all links, add them after
    // the `break-out` link.
    if (type === 'accordion' && oneAtATime !== 'true' && expandCollapseLink === 'visible') {
      const groupLinkWrapper = `
        <div class="accordion-group-control-wrapper" data-font="cnx-regular">
      `;

      const expandLink = `
        <button
          type="button"
          class="accordion-group-control accordion-expand-all-link spa-btn spa-btn--link"
          ${expandLinkBactmln ? `data-bactmln="${expandLinkBactmln}"` : ''}
        >${expandLinkText}<span class="ada-hidden"> panels</span></button>
      `;
      const linkDivider = '<span class="accordion-link-divider"> | </span>';
      const collapseLink = `
        <button
          type="button"
          class="accordion-group-control accordion-collapse-all-link spa-btn spa-btn--link"
          ${collapseLinkBactmln ? `data-bactmln="${collapseLinkBactmln}"` : ''}
        >${collapseLinkText}<span class="ada-hidden"> panels</span></button>
      `;

      accordionGroupControls
        += `${groupLinkWrapper}${expandLink}${linkDivider}${collapseLink}</div>`;
    }

    // Optional header colors.
    const headerColor = jcr.options.color ? jcr.options.color : '';

    // This will contain the horizontal tabs shown on medium-up screens.
    let accordionTablist = '<div class="accordion-tablist" role="button">';

    // This will hold our tab content and small screen "tab" headers.
    let accordionPanels = '<div class="accordion-panels">';

    // Append each panel instance to above elements.
    jcr.panels.forEach((panel, index) => {
      // Apply `active` class if present. Defaults to '' if not set in JCR.
      const activeClass = panel.active ? 'active' : '';
      const panelID = `${jcr.id}Panel${index}`;
      const tabID = `${jcr.id}Tab${index}`;
      const panelSlug = (panel.slug ? panel.slug : panelID);
      let panelDataAttributes = '';

      if (panel.data) {
        Object.keys(panel.data).forEach((attribute) => {
          panelDataAttributes += ` data-${attribute}="${panel.data[attribute]}"`;
        });
      }

      // Add tabs to the above `accordion-tablist`.
      accordionTablist += `
        <a
          href="javascript:void(0);"
          class="accordion-tab spa-oline ${activeClass}"
          id="${tabID}"
          data-panel="${panelID}"
          ${panelDataAttributes}
        >
          ${panel.label}
          <span class="ada-hidden"></span>
          <span
            class="panel-status-icon error"
            data-label="${STRINGS.DEFAULT_ADA_ERROR_TEXT}"
          ></span>
          <span
            class="panel-status-icon warning"
            data-label="${STRINGS.DEFAULT_ADA_WARNING_TEXT}"
          ></span>
          <span
            class="panel-status-icon success"
            data-label="${STRINGS.DEFAULT_ADA_SUCCESS_TEXT}"
          ></span>
        </a>
      `;

      // Build each panel header.
      const panelHeader = `
        <a
          name="${panel.label.replace(/[^a-zA-Z ]/g, '').replace(/ /g, '_')}"
          href="javascript:void(0);" class="panel-header spa-oline ${headerColor}"
          data-panel="${panelID}"
          ${panelDataAttributes}
          role="button"
        >
          <span class="spa-accordion-arrow spa-arrow-btn spa-arrow-btn--learn-more"></span>
          ${panel.label}
          <span class="ada-hidden"></span>
          <span
            class="panel-status-icon error"
            data-label="${STRINGS.DEFAULT_ADA_ERROR_TEXT}"
          ></span>
          <span
            class="panel-status-icon warning"
            data-label="${STRINGS.DEFAULT_ADA_WARNING_TEXT}"
          ></span>
          <span
            class="panel-status-icon success"
            data-label="${STRINGS.DEFAULT_ADA_SUCCESS_TEXT}"
          ></span>
        </a>
      `;

      // Build each panel content.
      // TODO: I have broken out the content element because I was hoping to inject a partial HBS
      // file into `contentMid`.
      const contentStart = `<div class="panel-content ${headerColor}" aria-labelledby="${tabID}">`;
      let contentMid = '';
      const contentEnd = '</div>';

      // TODO: Inner Panels loop. This should really be a recursive call of this same function. If
      // there is an inner accordion, It basically builds the same HTML as above, but adds a few
      // inner options or classes.
      if (typeof panel.innerPanels !== 'undefined') {
        // Set defaults if JCR options do not exist for this panel.
        const innerType = panel.innerPanels.type ? panel.innerPanels.type : 'tabs';
        const innerOneAtATime = panel.innerPanels.oneAtATime ? panel.innerPanels.oneAtATime : '';
        const innerExpandCollapseLink = panel.innerPanels.expandCollapseLink
          ? panel.innerPanels.expandCollapseLink
          : 'auto';
        const innerID = panel.innerPanels.id ? panel.innerPanels.id : `${jcr.id}Child${index}`;

        contentMid = `
          <div class="content">
            <div class="row">
              <div class="column">
                <section
                  class="accordion-wrapper innerPanels"
                  id="${innerID}"
                  data-accordion-type="${innerType}"
                  data-one-at-a-time="${innerOneAtATime}"
                >
        `;

        const innerTitleHeadingTag = panel.innerPanels.titleHeadingTag
          ? panel.innerPanels.titleHeadingTag
          : 'h4';
        const innerTitle = panel.innerPanels.title
          ? `<${innerTitleHeadingTag}>${panel.innerPanels.title}</${innerTitleHeadingTag}>`
          : '';

        // Hidden `break-out` link used to support display of all data for SEO.
        const innerBreakoutLink = `
          <a
            href="javascript:void(0);"
            class="spa-hidden break-out"
            aria-hidden="true"
            data-breakout-link="${innerID}"
          >Expand all panels</a>
        `;

        // Include the `break-out` links for all accordion groups.
        let innerAccordionGroupControls = `${innerBreakoutLink}`;

        // If the inner accordion group is configured with visible expand/collapse all links, add
        // them after the `break-out` link.
        if (
          innerType === 'accordion'
          && innerOneAtATime !== 'true'
          && innerExpandCollapseLink === 'visible'
        ) {
          const innerGroupLinkWrapper = `
            <div class="accordion-group-control-wrapper" data-font="cnx-regular">
          `;

          const innerExpandLink = `
            <button
              type="button"
              class="accordion-group-control accordion-expand-all-link spa-btn spa-btn--link"
            >${expandLinkText}<span class="ada-hidden"> panels</span></button>
          `;
          const innerLinkDivider = '<span class="accordion-link-divider"> | </span>';
          const innerCollapseLink = `
            <button
              type="button"
              class="accordion-group-control accordion-collapse-all-link spa-btn spa-btn--link"
            >${collapseLinkText}<span class="ada-hidden"> panels</span></button>
          `;

          innerAccordionGroupControls += `
              ${innerGroupLinkWrapper}${innerExpandLink}${innerLinkDivider}${innerCollapseLink}
            </div>
          `;
        }

        const innerColor = panel.innerPanels.color ? panel.innerPanels.color : '';
        let innerTabList = '<div class="accordion-tablist">';
        let innerPanels = '<div class="accordion-panels">';

        panel.content.forEach((innerPanel, innerIndex) => {
          const innerPanelID = `${innerID}Panel${innerIndex}`;
          const innerTabID = `${innerID}Tab${innerIndex}`;
          const innerPanelSlug = (innerPanel.slug ? innerPanel.slug : innerPanelID);
          let innerPanelDataAttributes = '';

          if (innerPanel.data) {
            Object.keys(innerPanel.data).forEach((attribute) => {
              innerPanelDataAttributes += ` data-${attribute}="${innerPanel.data[attribute]}"`;
            });
          }

          // Account for introductory text before the nested panels.
          if (typeof innerPanel === 'string') {
            innerPanels += innerPanel;
            return;
          }

          const innerActiveClass = innerPanel.active ? 'active' : '';

          innerTabList += `
            <a
              href="javascript:void(0);"
              class="accordion-tab spa-oline ${innerActiveClass}"
              id="${innerTabID}"
              data-panel="${innerPanelID}"${innerPanelDataAttributes}
            >
              ${innerPanel.label}
              <span class="ada-hidden"></span>
              <span
                class="panel-status-icon error"
                data-label="${STRINGS.DEFAULT_ADA_ERROR_TEXT}"
              ></span>
              <span
                class="panel-status-icon warning"
                data-label="${STRINGS.DEFAULT_ADA_WARNING_TEXT}"
              ></span>
              <span
                class="panel-status-icon success"
                data-label="${STRINGS.DEFAULT_ADA_SUCCESS_TEXT}"
              ></span>
            </a>
          `;

          const innerHeader = `
            <a
              href="javascript:void(0);"
              class="panel-header spa-oline ${innerColor}"
              data-panel="${innerPanelID}"
              ${innerPanelDataAttributes}
              role="button"
              data-breakout
            >
              <span
                class="spa-accordion-arrow spa-arrow-btn spa-arrow-btn--learn-more"
                data-breakout
              ></span>
              ${innerPanel.label}
              <span class="ada-hidden"></span>
              <span
                class="panel-status-icon error"
                data-label="${STRINGS.DEFAULT_ADA_ERROR_TEXT}"
              ></span>
              <span
                class="panel-status-icon warning"
                data-label="${STRINGS.DEFAULT_ADA_WARNING_TEXT}"
              ></span>
              <span
                class="panel-status-icon success"
                data-label="${STRINGS.DEFAULT_ADA_SUCCESS_TEXT}"
              ></span>
            </a>
          `;

          let innerContent;
          const startingHTML = `
            <div
              class="panel-content panel-content--nested ${innerColor}"
              aria-labelledby="${innerTabID}"
            >
          `;
          const endingHTML = '</div></div></div>';
          if (innerPanel.dynamicContent === 'partial') {
            // Partials will be loaded from the default locations for Handlebars templates
            // (e.g. templates, templates/partials from the module folder)
            const templateString = `
              ${startingHTML}
              <div class="row collapse">
                <div class="column">
                  {{> ${innerPanel.partialName}}}
              ${endingHTML}
            `;
            const partialTemplate = Handlebars.compile(templateString);

            innerContent = getDynamicContent(innerPanel, jcrContexts, partialTemplate);
          } else if (innerPanel.dynamicContent === 'markdown') {
            const templateString = `
              ${startingHTML}
              <div class="row collapse">
                <div class="column">
                  {{{content.markdown}}}
              ${endingHTML}
            `;
            const partialTemplate = Handlebars.compile(templateString);

            innerContent = getDynamicContent(innerPanel, jcrContexts, partialTemplate);
          } else if (innerPanel.dynamicContent === 'module') {
            if (jcrContexts.length !== 0 && innerPanel.moduleId) {
              let moduleContent = null;

              jcrContexts.every((context) => {
                if (Object.prototype.hasOwnProperty.call(context, innerPanel.moduleId)) {
                  moduleContent = context[innerPanel.moduleId];
                  return false;
                }
                return true;
              });

              if (moduleContent === null) {
                contentMid = `
                  ${startingHTML}
                  <div class="content">
                    <div class="row collapse">
                      <div class="column">${STRINGS.NO_PANEL_CONTENT_ERROR}</div>
                  ${endingHTML}
                `;
              } else {
                contentMid = `
                  ${startingHTML}
                  <div class="content">
                    <div class="row collapse">
                      <div class="column">${moduleContent}</div>
                  ${endingHTML}
                `;
              }
            } else if (innerPanel.moduleName) {
              contentMid = `
                ${startingHTML}
                <div class="content">
                  <div class="row collapse">
                    <div class="column">
                      <div data-panel-content="${innerPanel.moduleName}"></div>
                    </div>
                ${endingHTML}
              `;
            } else {
              contentMid = `
                ${startingHTML}
                <div class="content">
                  <div class="row collapse">
                    <div class="column">${STRINGS.NO_PANEL_CONTENT_ERROR}</div>
                ${endingHTML}
              `;
            }
          } else {
            innerContent = `
              ${startingHTML}
              <div class="content">
                <div class="row collapse">
                  <div class="column">${innerPanel.content}</div>
              ${endingHTML}
            `;
          }

          innerPanels += `
            <div
              class="accordion-panel ${innerActiveClass}"
              id="${innerPanelID}"
              data-slug="${innerPanelSlug}"
            >${innerHeader}${innerContent}</div>
          `;
        });

        contentMid += `
                      ${innerTitle}${innerAccordionGroupControls}${innerTabList}
                    </div>
                    ${innerPanels}
                  </div>
                </section>
              </div>
            </div>
          </div>
        `;
      } else if (panel.dynamicContent === 'partial') {
        // Partials will be loaded from the default locations for Handlebars templates
        // (e.g. templates, templates/partials from the module folder)
        const templateString = `
          <div class="content">
            <div class="row">
              <div class="column">{{> ${panel.partialName}}}</div>
            </div>
          </div>
        `;
        const partialTemplate = Handlebars.compile(templateString);

        contentMid = getDynamicContent(panel, jcrContexts, partialTemplate);
      } else if (panel.dynamicContent === 'markdown') {
        const templateString = `
          <div class="content">
            <div class="row">
              <div class="column">{{{content.markdown}}}</div>
            </div>
          </div>
        `;
        const partialTemplate = Handlebars.compile(templateString);

        contentMid = getDynamicContent(panel, jcrContexts, partialTemplate);
      } else if (panel.dynamicContent === 'module') {
        if (jcrContexts.length !== 0 && panel.moduleId) {
          let moduleContent = null;

          jcrContexts.every((context) => {
            if (Object.prototype.hasOwnProperty.call(context, panel.moduleId)) {
              moduleContent = context[panel.moduleId];
              return false;
            }
            return true;
          });

          if (moduleContent === null) {
            contentMid = `
              <div class="content">
                <div class="row">
                  <div class="column">${STRINGS.NO_PANEL_CONTENT_ERROR}</div>
                </div>
              </div>
            `;
          } else {
            contentMid = `
              <div class="content">
                <div class="row collapse">
                  <div class="column">${moduleContent}</div>
                </div>
              </div>
            `;
          }
        } else if (panel.moduleName) {
          contentMid = `
            <div class="content">
              <div class="row collapse">
                <div class="column">
                  <div data-panel-content="${panel.moduleName}"></div>
                </div>
              </div>
            </div>
          `;
        } else {
          contentMid = `
            <div class="content">
              <div class="row">
                <div class="column">${STRINGS.NO_PANEL_CONTENT_ERROR}</div>
              </div>
            </div>
          `;
        }
      } else {
        contentMid = Array.isArray(panel.content) ? panel.content.join('') : panel.content;
      }

      const panelContent = `${contentStart}${contentMid}${contentEnd}`;

      // Add each panel instance to a container.
      accordionPanels += `
        <div
          class="accordion-panel ${activeClass}"
          id="${panelID}"
          data-slug="${panelSlug}"
        >${panelHeader}${panelContent}</div>
      `;
    });

    // Compile all the accordion HTML.
    accordionWrap += `
            ${accordionTitle}${accordionGroupControls}${accordionTablist}
          </div>
          ${accordionPanels}
        </div>
      </section>
    `;

    return new Handlebars.SafeString(accordionWrap);
  },
};

/**
 * @class accordionHelper
 * @description Class used to register our helper with Handlebars.
 */
class accordionHelper {
  /**
   * @method register
   * @param {object} Handlebars Reference to Handlebars for helper registration.
   * @description Registers helper with Handlebars.
   * @returns {void}
   */
  static register(Handlebars) {
    Object.keys(helpers).forEach((key) => {
      Handlebars.registerHelper(key, helpers[key]);
    });
  }
}

module.exports = accordionHelper;
